import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useKeysQuery } from "@/entities/keys/queries";
import { useCreateProjectMutation } from "@/entities/projects/queries";
import { ApiError } from "@/shared/api/http";
import { PageHeader } from "@/widgets/PageHeader";
import {
  Stepper,
  Step1Input,
  Step2Depth,
  type Step1Value,
  type Step2Value,
} from "@/widgets/ProjectWizard";

const STEPS = ["Что анализируем", "Глубина и бюджет"] as const;

const DEFAULT_STEP1: Step1Value = {
  input_type: "query",
  input_value: "",
  region_id: 225,
  exclude_terms: [],
  name: "",
};

// Дефолты под пресет «Стандарт» — чтобы Step2 сразу выглядел рабочим.
const DEFAULT_STEP2: Step2Value = {
  target_phrases: 500,
  expansion_depth: 1,
  dynamics_top_k: 30,
};

export function NewProjectPage(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = React.useState(0);
  const [s1, setS1] = React.useState<Step1Value>(DEFAULT_STEP1);
  const [s2, setS2] = React.useState<Step2Value>(DEFAULT_STEP2);

  const keysQ = useKeysQuery();
  // OpenAI нужен только для niche/site (LLM-сиды) и для генерации идей
  // в анализах. Для query (морфология) — Yandex достаточно.
  const needsOpenai = s1.input_type === "niche" || s1.input_type === "site";
  const yandexReady = !!keysQ.data?.yandex.valid;
  const openaiReady = !!keysQ.data?.openai.valid;
  const keysReady = yandexReady && (!needsOpenai || openaiReady);
  const missingKeys: ("yandex" | "openai")[] = [
    ...(yandexReady ? [] : (["yandex"] as const)),
    ...(needsOpenai && !openaiReady ? (["openai"] as const) : []),
  ];

  const createM = useCreateProjectMutation();

  const launch = (): void => {
    createM.mutate(
      {
        name: s1.name.trim() || s1.input_value.trim(),
        input_type: s1.input_type,
        input_value: s1.input_value.trim(),
        region_id: s1.region_id,
        exclude_terms: s1.exclude_terms,
        target_phrases: s2.target_phrases,
        expansion_depth: s2.expansion_depth,
        dynamics_top_k: s2.dynamics_top_k,
      },
      {
        onSuccess: (out) => {
          toast.success("Проект создан — сбор запущен");
          navigate(`/projects/${out.project.id}`);
        },
        onError: (e) => {
          const msg =
            e instanceof ApiError ? e.message : "Не удалось создать проект";
          toast.error(msg);
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={<>Новый <span className="text-gradient">проект</span></>}
        subtitle="Собираем семантическое ядро один раз — потом делаем из него любые срезы"
      />

      <div className="mb-8">
        <Stepper steps={STEPS} current={step} />
      </div>

      {step === 0 ? (
        <Step1Input
          value={s1}
          onChange={setS1}
          onNext={() => setStep(1)}
        />
      ) : (
        <Step2Depth
          value={s2}
          onChange={setS2}
          onBack={() => setStep(0)}
          onLaunch={launch}
          launching={createM.isPending}
          keysReady={keysReady}
          missingKeys={missingKeys}
        />
      )}
    </div>
  );
}
